import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-hot-toast';
import {
  Mail, Phone, MapPin, Send, Clock, CheckCircle2,
  Linkedin, Twitter, Facebook
} from 'lucide-react';
import { contactsService } from '../../services/api';

const validationSchema = Yup.object({
  companyName: Yup.string().required('Nom requis').min(2, 'Minimum 2 caractères'),
  companyType: Yup.string(),
  activitySector: Yup.string().required('Secteur requis'),
  contactName: Yup.string().required('Nom requis').min(2, 'Minimum 2 caractères'),
  contactFunction: Yup.string(),
  contactEmail: Yup.string().email('Email invalide').required('Email requis'),
  contactPhone: Yup.string(),
  serviceType: Yup.string().required('Type de service requis'),
  projectDescription: Yup.string().required('Description requise').min(10, 'Minimum 10 caractères'),
  desiredDeadline: Yup.string(),
});

function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState('Chargement...');
  const [phone, setPhone] = useState('Chargement...');
  const [address, setAddress] = useState('Chargement...');

  // Récupération des données dynamiques
  const fetchContactInfo = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/settings`);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const result = await res.json();

      if (result.settings) {
        setEmail(result.settings.contact_email || 'contact@ddec.ci');
        setPhone(result.settings.contact_phone || '+225 07 67 81 19 87');
        setAddress(result.settings.contact_address || 'Abidjan, Côte d\'Ivoire');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des informations de contact :', err);
      // Valeurs par défaut en cas d'erreur
      setEmail('contact@ddec.ci');
      setPhone('+225 07 67 81 19 87');
      setAddress('Abidjan, Côte d\'Ivoire');
    }
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const formik = useFormik({
    initialValues: {
      companyName: '',
      companyType: '',
      activitySector: '',
      contactName: '',
      contactFunction: '',
      contactEmail: '',
      contactPhone: '',
      serviceType: '',
      projectDescription: '',
      desiredDeadline: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await contactsService.send(values);
        setIsSubmitted(true);
        toast.success('Demande envoyée avec succès !');
        formik.resetForm();
      } catch (error) {
        console.error('Erreur:', error);
        toast.error('Erreur lors de l\'envoi de la demande');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const activitySectors = [
    'Banque',
    'Assurance',
    'Microfinance',
    'Fintech',
    'Autre'
  ];

  const serviceTypes = [
    'Conseil ISO 20022',
    'Solutions WealthTech',
    'Core Banking',
    'Conformité',
    'Formation',
    'Autre'
  ];

  const deadlines = [
    'Dès que possible',
    'Dans les 2 semaines',
    'Dans le mois',
    'Pas de date précise'
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-dark-900 via-primary-900 to-dark-900 overflow-hidden">
        <div className="absolute inset-0 hero-pattern opacity-10" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-primary-200 mb-6">
              <Mail className="w-4 h-4" />
              Contact
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Parlons de votre{' '}
              <span className="text-primary-400">projet</span>
            </h1>
            <p className="text-xl text-dark-300">
              Contactez-nous pour discuter de vos besoins et obtenir un audit gratuit
              de votre infrastructure.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-1 gap-12">
            {/* Contact Form - inchangé */}
            <motion.div
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              className="w-full"
            >
              {isSubmitted ? (
                <div className="bg-white p-8 rounded-xl shadow-soft border border-dark-100 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-dark-900 mb-4">
                    Demande envoyée !
                  </h3>
                  <p className="text-dark-600 mb-6">
                    Merci pour votre demande. Notre équipe vous répondra dans les 24 heures.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-primary"
                  >
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
                <form onSubmit={formik.handleSubmit} className="bg-white p-8 rounded-xl shadow-soft border border-dark-100">
                  {/* Informations sur l'entreprise */}
                  <div className="border-b border-dark-100 pb-6 mb-6">
                    <h3 className="text-xl font-semibold text-dark-900 mb-6 flex items-center gap-2">
                      🏢 Informations sur l'entreprise
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Nom de l'entreprise *
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formik.values.companyName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.companyName && formik.errors.companyName ? 'border-red-500' : 'border-dark-300'}`}
                          placeholder="Ex: Ma Société SARL"
                        />
                        {formik.touched.companyName && formik.errors.companyName && (
                          <p className="mt-1 text-sm text-red-600">{formik.errors.companyName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Type d'entreprise
                        </label>
                        <input
                          type="text"
                          name="companyType"
                          value={formik.values.companyType}
                          onChange={formik.handleChange}
                          className="w-full px-4 py-2 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Ex: SARL, SA, etc"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Secteur d'activité *
                      </label>
                      <select
                        name="activitySector"
                        value={formik.values.activitySector}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.activitySector && formik.errors.activitySector ? 'border-red-500' : 'border-dark-300'}`}
                      >
                        <option value="">Sélectionner un secteur</option>
                        {activitySectors.map((sector, index) => (
                          <option key={index} value={sector}>{sector}</option>
                        ))}
                      </select>
                      {formik.touched.activitySector && formik.errors.activitySector && (
                        <p className="mt-1 text-sm text-red-600">{formik.errors.activitySector}</p>
                      )}
                    </div>
                  </div>

                  {/* Personne de contact */}
                  <div className="border-b border-dark-100 pb-6 mb-6">
                    <h3 className="text-xl font-semibold text-dark-900 mb-6 flex items-center gap-2">
                      👤 Personne de contact
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          name="contactName"
                          value={formik.values.contactName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.contactName && formik.errors.contactName ? 'border-red-500' : 'border-dark-300'}`}
                          placeholder="Votre nom complet"
                        />
                        {formik.touched.contactName && formik.errors.contactName && (
                          <p className="mt-1 text-sm text-red-600">{formik.errors.contactName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Fonction
                        </label>
                        <input
                          type="text"
                          name="contactFunction"
                          value={formik.values.contactFunction}
                          onChange={formik.handleChange}
                          className="w-full px-4 py-2 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="Votre fonction"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="contactEmail"
                          value={formik.values.contactEmail}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.contactEmail && formik.errors.contactEmail ? 'border-red-500' : 'border-dark-300'}`}
                          placeholder="votre@email.com"
                        />
                        {formik.touched.contactEmail && formik.errors.contactEmail && (
                          <p className="mt-1 text-sm text-red-600">{formik.errors.contactEmail}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          name="contactPhone"
                          value={formik.values.contactPhone}
                          onChange={formik.handleChange}
                          className="w-full px-4 py-2 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          placeholder="+225 XX XX XX XX"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Demande de service */}
                  <div>
                    <h3 className="text-xl font-semibold text-dark-900 mb-6 flex items-center gap-2">
                      📋 Demande de service
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Type de service *
                        </label>
                        <select
                          name="serviceType"
                          value={formik.values.serviceType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.serviceType && formik.errors.serviceType ? 'border-red-500' : 'border-dark-300'}`}
                        >
                          <option value="">Sélectionner un service</option>
                          {serviceTypes.map((service, index) => (
                            <option key={index} value={service}>{service}</option>
                          ))}
                        </select>
                        {formik.touched.serviceType && formik.errors.serviceType && (
                          <p className="mt-1 text-sm text-red-600">{formik.errors.serviceType}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-dark-700 mb-1">
                          Délai souhaité
                        </label>
                        <select
                          name="desiredDeadline"
                          value={formik.values.desiredDeadline}
                          onChange={formik.handleChange}
                          className="w-full px-4 py-2 border border-dark-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        >
                          <option value="">Sélectionner un délai</option>
                          {deadlines.map((deadline, index) => (
                            <option key={index} value={deadline}>{deadline}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-dark-700 mb-1">
                        Description du projet *
                      </label>
                      <textarea
                        name="projectDescription"
                        value={formik.values.projectDescription}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={5}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${formik.touched.projectDescription && formik.errors.projectDescription ? 'border-red-500' : 'border-dark-300'}`}
                        placeholder="Décrivez votre projet, vos besoins et vos objectifs..."
                      />
                      {formik.touched.projectDescription && formik.errors.projectDescription && (
                        <p className="mt-1 text-sm text-red-600">{formik.errors.projectDescription}</p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-dark-900 hover:bg-dark-800 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner w-5 h-5 mr-2" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer la demande
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Info Section - uniquement cette partie est modifiée pour les données dynamiques */}
      <section className="bg-dark-50 py-12">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Email */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-dark-100 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-semibold text-dark-900 mb-2">Envoyez-nous un email</h3>
              <a href={`mailto:${email}`} className="text-primary-600 hover:underline font-medium">
                {email}
              </a>
            </div>

            {/* Téléphone */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-dark-100 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-semibold text-dark-900 mb-2">Appelez-nous</h3>
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className="text-primary-600 hover:underline font-medium">
                {phone}
              </a>
            </div>

            {/* Adresse */}
            <div className="bg-white p-6 rounded-xl shadow-soft border border-dark-100 text-center">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="font-semibold text-dark-900 mb-2">Visitez-nous</h3>
              <p className="text-dark-600 font-medium">{address}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
